import React, { useState, useRef, useEffect } from "react";
import { api } from "../../lib/api";
import { 
  Mic, MicOff, Sparkles, Check, X, ChevronDown, ChevronUp,
  AlertTriangle, Send, Loader2, Volume2, RefreshCw
} from "lucide-react";

export default function CareNoteEntry({
  prompt,
  entry,
  onSave,
  onPolish,
  isLoading,
}) {
  const [response, setResponse] = useState(entry?.final_response || "");
  const [originalResponse, setOriginalResponse] = useState(entry?.original_response || "");
  const [polishedResponse, setPolishedResponse] = useState(entry?.ai_polished_response || null);
  const [showPolishDiff, setShowPolishDiff] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPolishing, setIsPolishing] = useState(false);
  const [isNotApplicable, setIsNotApplicable] = useState(entry?.is_not_applicable || false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [expanded, setExpanded] = useState(!!response || prompt.required);
  const [responseMethod, setResponseMethod] = useState(entry?.response_method || "typed");
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [response]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        await handleTranscription();
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setError(null);
    } catch (err) {
      setError("Microphone access denied. Please allow microphone access.");
      console.error("Failed to start recording:", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleTranscription = async () => {
    if (audioChunksRef.current.length === 0) return;
    
    setIsTranscribing(true);
    setError(null);
    
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      
      const { data } = await api.post("/care-notes/transcribe", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setResponse(data.text);
      setOriginalResponse(data.text);
      setResponseMethod("voice");
      setPolishedResponse(null);
      setShowPolishDiff(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Transcription failed");
    } finally {
      setIsTranscribing(false);
    }
  };

  const handlePolish = async () => {
    if (!response.trim()) return;
    
    setIsPolishing(true);
    setError(null);
    
    try {
      const { data } = await api.post("/care-notes/polish", {
        text: response,
        context: prompt.prompt_text,
      });
      
      if (data.changed) {
        setPolishedResponse(data.polished_text);
        setShowPolishDiff(true);
      } else {
        setError("Text is already well-written!");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "AI polish failed");
    } finally {
      setIsPolishing(false);
    }
  };

  const acceptPolish = () => {
    setResponse(polishedResponse);
    setShowPolishDiff(false);
    handleSave(polishedResponse, true);
  };

  const rejectPolish = () => {
    setPolishedResponse(null);
    setShowPolishDiff(false);
  };

  const handleSave = (text = response, wasPolished = false) => {
    if (isNotApplicable || text.trim()) {
      onSave({
        prompt_key: prompt.key,
        prompt_text: prompt.prompt_text,
        response: text,
        response_method: responseMethod,
        is_not_applicable: isNotApplicable,
        is_urgent: isUrgent,
        ai_polished: wasPolished,
      });
    }
  };

  const toggleNotApplicable = () => {
    const newValue = !isNotApplicable;
    setIsNotApplicable(newValue);
    if (newValue) {
      setResponse("");
      handleSave("", false);
    }
  };

  if (!expanded && !prompt.required) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full text-left p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors"
        data-testid={`expand-prompt-${prompt.key}`}
      >
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-700">{prompt.prompt_text}</span>
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </div>
      </button>
    );
  }

  return (
    <div 
      className={`bg-white rounded-xl border ${isUrgent ? "border-red-300 ring-1 ring-red-200" : "border-gray-200"} overflow-hidden`}
      data-testid={`care-note-entry-${prompt.key}`}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{prompt.prompt_text}</span>
            {prompt.required && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Required</span>
            )}
          </div>
          {!prompt.required && (
            <button
              onClick={() => setExpanded(false)}
              className="p-1 rounded hover:bg-gray-200 transition-colors"
            >
              <ChevronUp className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* N/A Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isNotApplicable}
            onChange={toggleNotApplicable}
            className="w-4 h-4 rounded border-gray-300 text-[#06372B] focus:ring-[#06372B]"
            data-testid={`na-checkbox-${prompt.key}`}
          />
          <span className="text-sm text-gray-600">Not applicable to this visit</span>
        </label>

        {!isNotApplicable && (
          <>
            {/* Text Area */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={response}
                onChange={(e) => {
                  setResponse(e.target.value);
                  setResponseMethod("typed");
                  setPolishedResponse(null);
                }}
                placeholder={prompt.placeholder}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] outline-none transition-colors resize-none text-sm"
                data-testid={`textarea-${prompt.key}`}
              />
              
              {/* Recording indicator */}
              {isRecording && (
                <div className="absolute top-2 right-2 flex items-center gap-2 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  Recording...
                </div>
              )}
              
              {isTranscribing && (
                <div className="absolute top-2 right-2 flex items-center gap-2 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Transcribing...
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Polish Diff View */}
            {showPolishDiff && polishedResponse && (
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200">
                <div className="flex items-center gap-2 text-purple-700 font-medium">
                  <Sparkles className="w-4 h-4" />
                  AI Suggestion
                </div>
                
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-white border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Original</div>
                    <div className="text-sm text-gray-700">{originalResponse}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-purple-200">
                    <div className="text-xs text-purple-600 mb-1">Improved</div>
                    <div className="text-sm text-gray-900">{polishedResponse}</div>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500">
                  AI only improved grammar and tone. No facts were added or changed.
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={acceptPolish}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors"
                    data-testid={`accept-polish-${prompt.key}`}
                  >
                    <Check className="w-4 h-4" />
                    Use Improved
                  </button>
                  <button
                    onClick={rejectPolish}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-white border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    data-testid={`reject-polish-${prompt.key}`}
                  >
                    <X className="w-4 h-4" />
                    Keep Original
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Voice Recording */}
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                className={`flex items-center gap-2 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                  isRecording
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                data-testid={`voice-btn-${prompt.key}`}
              >
                {isRecording ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    Stop
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    Voice
                  </>
                )}
              </button>

              {/* AI Polish */}
              <button
                onClick={handlePolish}
                disabled={!response.trim() || isPolishing || showPolishDiff}
                className="flex items-center gap-2 py-2 px-4 rounded-lg bg-purple-50 text-purple-700 font-medium text-sm hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`polish-btn-${prompt.key}`}
              >
                {isPolishing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Polish
              </button>

              {/* Urgent Flag (only for concerns prompt) */}
              {prompt.can_flag_urgent && response.trim() && (
                <label className="flex items-center gap-2 py-2 px-4 rounded-lg bg-red-50 text-red-700 cursor-pointer hover:bg-red-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={isUrgent}
                    onChange={(e) => setIsUrgent(e.target.checked)}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                    data-testid={`urgent-checkbox-${prompt.key}`}
                  />
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Flag Urgent</span>
                </label>
              )}

              {/* Save Button */}
              <button
                onClick={() => handleSave()}
                disabled={isLoading || (!response.trim() && !isNotApplicable)}
                className="flex items-center gap-2 py-2 px-4 rounded-lg bg-[#06372B] text-white font-medium text-sm hover:bg-[#0a4a3a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
                data-testid={`save-btn-${prompt.key}`}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Save
              </button>
            </div>

            {/* Response method indicator */}
            {response && responseMethod === "voice" && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Volume2 className="w-3 h-3" />
                Recorded via voice
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
